import {
  CoverageCalculator,
  CoverageCalculatorOptions,
  CoveragePriceResult,
  CustomerInfo,
  GetCoverageParams,
  GetCoverageResult,
  getItemEligibilityInfoParams,
  isProductEnabledParams,
  ItemInfo,
} from ".";
import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
} from "../is-coverage-fee-product";
import {
  cartTotalValidForProtection,
  getVariantMetafieldIdentifiers,
  itemHasInvalidMetafields,
  itemValidForProtection,
} from "./check-product";
import { CoverageIneligibilityReason } from "./ineligibility-reasons";
import {
  getCoverageMlModelInference,
  getPriceBracketPrice,
} from "./utils/get-price-bracket-price";
import { getPricingRulesInLocalCurrency } from "./utils/get-pricing-rules-in-local-currency";
import { listsHaveMatchingElement } from "./utils/lists-have-matching-element";

const isProductEnabled = ({ team }: isProductEnabledParams) => {
  return !!(
    team.settings.packageProtection?.enabled &&
    team.settings.packageProtection?.coverage
  );
};

const getShopperEligibilityInfo = (
  customerInfo: CustomerInfo,
  team: CoverageCalculatorOptions,
) => {
  if (
    listsHaveMatchingElement(
      customerInfo.customerTags,
      team.settings.coverageExcludeCustomerTags,
    )
  ) {
    return {
      isEligible: false,
      reasonIneligible: CoverageIneligibilityReason.CUSTOMER_TAGS,
    };
  }

  const packageEligibleCountries =
    getAllPackageProtectionEligibleCountries(team);

  const ValidCountry =
    packageEligibleCountries.includes(customerInfo.country) ||
    packageEligibleCountries.length === 0;
  if (!ValidCountry) {
    return {
      isEligible: false,
      reasonIneligible: CoverageIneligibilityReason.SHOPPER_INELIGIBLE_COUNTRY,
    };
  }

  if (
    customerInfo.isBusinessAccount &&
    team.settings.shopifyExtension?.disableBusinessAccountInStorefront
  ) {
    return {
      isEligible: false,
      reasonIneligible: CoverageIneligibilityReason.BUSINESS_ACCOUNT_EXCLUDED,
    };
  }

  if (customerInfo.usingInStorePickup) {
    return {
      isEligible: false,
      reasonIneligible: CoverageIneligibilityReason.IN_STORE_PICKUP_EXCLUDED,
    };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

const getItemEligibilityInfo = ({
  item,
  team,
  redoCoverageProductIds,
}: getItemEligibilityInfoParams) => {
  if (
    isCoverageFeeProduct({
      product: item,
      redoCoverageProductIds: new Set(redoCoverageProductIds ?? []),
      matchType: CoverageFeeProductMatchType.REDO_ONLY,
    })
  ) {
    return {
      isEligible: false,
      reasonIneligible: CoverageIneligibilityReason.REDO_PRODUCT_EXCLUDED,
    };
  }

  if (item.requiresShipping === false) {
    return {
      isEligible: false,
      reasonIneligible:
        CoverageIneligibilityReason.ITEM_DOES_NOT_REQUIRE_SHIPPING,
    };
  }

  const excludedCollections =
    team.settings.packageProtection?.coverageExcludedCollections ?? [];
  const itemValidResult = itemValidForProtection({
    itemInfo: {
      productTags: item.productTags,
      collections: item.collections,
      properties: item.properties,
    },
    invalidProductTags: team.settings.protectionExcludeTags ?? [],
    invalidCollections: excludedCollections,
  });

  if (!itemValidResult.valid) {
    return { isEligible: false, reasonIneligible: itemValidResult.reason };
  }

  const invalidMetafields = getVariantMetafieldIdentifiers(
    team.settings.packageProtection?.coverageExcludedVariantMetafields ?? [],
  );
  if (
    itemHasInvalidMetafields({
      itemMetafields: item.variantMetafields,
      invalidMetafields,
    })
  ) {
    return {
      isEligible: false,
      reasonIneligible:
        CoverageIneligibilityReason.ITEM_INVALID_VARIANT_METAFIELDS,
    };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

// returns a list of countries. Empty list means all countries
function getAllPackageProtectionEligibleCountries(
  team: CoverageCalculatorOptions,
) {
  // Match autocheck logic exactly:
  // If pricingRuleSet[1] exists (international pricing), return [] (all countries eligible)
  // Otherwise, use pricingRuleSet[0].countries, falling back to dedicated countries field
  if (team.settings.packageProtection?.pricingRuleSet?.[1]) {
    return [];
  }

  const countriesFromPricing =
    team.settings.packageProtection?.pricingRuleSet?.[0]?.countries;
  const countriesFromDedicatedField =
    team.settings?.packageProtection?.countries;

  return countriesFromPricing ?? countriesFromDedicatedField ?? [];
}

const calculateCoveragePrice = ({
  team,
  country,
  numProducts,
  amount,
  conversionRate,
  hasSubscriptionItem,
  priceOverrideForCoverageMlModelCacheOnly,
  priceCalculationContext,
}: {
  team: CoverageCalculatorOptions;
  country: string | undefined;
  numProducts: number;
  amount: number;
  conversionRate?: number;
  hasSubscriptionItem?: boolean;
  priceOverrideForCoverageMlModelCacheOnly?: { price: number };
  priceCalculationContext?: { checkoutProbability: number | undefined };
}): CoveragePriceResult => {
  if (conversionRate) {
    if (team.settings.packageProtection?.minPrice) {
      team.settings.packageProtection.minPrice *= conversionRate;
    }
    if (team.settings.packageProtection?.maxPrice) {
      team.settings.packageProtection.maxPrice *= conversionRate;
    }

    if (team.settings.packageProtection?.pricingRuleSet) {
      team.settings.packageProtection.pricingRuleSet =
        getPricingRulesInLocalCurrency(
          team.settings.packageProtection.pricingRuleSet,
          conversionRate,
        );
    }
  }

  if (team.settings.packageProtection) {
    const ruleSets = team.settings.packageProtection?.pricingRuleSet;
    const minPrice = team.settings.packageProtection.minPrice;
    const maxPrice = team.settings.packageProtection.maxPrice;
    const backupPercentage = team.settings.packageProtection.percentage;
    const fixedSubscriptionPrice =
      team.settings.packageProtection.fixedSubscriptionPrice ?? undefined;
    const coverageMlModel = getCoverageMlModelInference({
      mlModel: team.settings.packageProtection.coverageMlModel ?? undefined,
      sessionInferenceParams: {
        checkoutProbability: priceCalculationContext?.checkoutProbability,
      },
    });

    return getPriceBracketPrice({
      cartTotal: amount,
      pricingRuleSets: ruleSets,
      country,
      minPrice,
      maxPrice,
      backupPercentage,
      fixedSubscriptionPrice,
      cartHasSubscriptionItem: hasSubscriptionItem,
      coverageMlModel,
      priceOverrideForCoverageMlModelCacheOnly,
    });
  }

  return { price: 0, source: "unspecified" };
};

// returns a price if coverage is valid. otherwise returns undefined
const getCoverage = ({
  team,
  customerInfo,
  items,
  conversionRate,
  priceOverrideForCoverageMlModelCacheOnly,
  ignoreMerchantPaidForEnablementCheck,
  commentsoldIntegration,
  priceCalculationContext,
  redoCoverageProductIds,
}: GetCoverageParams): GetCoverageResult => {
  if (
    !isProductEnabled({
      team,
      ignoreMerchantPaidForEnablementCheck,
      commentsoldIntegration,
    })
  ) {
    return {
      eligible: false,
      priceResult: undefined,
      reasonIneligible: CoverageIneligibilityReason.PRODUCT_NOT_ENABLED,
      eligibleItems: [],
    };
  }

  const shopperEligibleInfo = getShopperEligibilityInfo(customerInfo, team);
  if (!shopperEligibleInfo.isEligible) {
    return {
      eligible: false,
      priceResult: undefined,
      reasonIneligible: shopperEligibleInfo.reasonIneligible,
      eligibleItems: [],
    };
  }

  const usePreDiscountCartValue =
    team.settings.shopifyExtension?.cartValueCalculationMethod ===
    "cart_value_pre_discount";

  let totalPrice = 0;
  let totalValidPrice = 0;
  let numValidItems = 0;
  let numInvalidItems = 0;
  const eligibleItems: ItemInfo[] = [];

  const ineligibleItemReasons = [];

  for (const item of items) {
    const unitPrice = usePreDiscountCartValue
      ? item.preDiscountPrice
      : item.price;
    const itemPrice = unitPrice * item.quantity;
    totalPrice += itemPrice;
    const itemEligibleInfo = getItemEligibilityInfo({
      item,
      team,
      commentsoldIntegration,
      redoCoverageProductIds,
    });
    if (itemEligibleInfo.isEligible) {
      totalValidPrice += itemPrice;
      numValidItems += item.quantity;
      eligibleItems.push(item);
    } else {
      numInvalidItems += item.quantity;
      ineligibleItemReasons.push(itemEligibleInfo.reasonIneligible);
    }
  }

  const cartEligibleInfo = getCartEligibilityInfo(
    numValidItems,
    numInvalidItems,
    totalPrice,
    team,
  );
  if (!cartEligibleInfo.isEligible) {
    return {
      eligible: false,
      priceResult: undefined,
      reasonIneligible:
        cartEligibleInfo.reasonIneligible || ineligibleItemReasons.join(", "),
      eligibleItems: [],
    };
  }

  const priceResult = calculateCoveragePrice({
    team,
    country: customerInfo.country,
    numProducts: numValidItems,
    amount: totalValidPrice,
    conversionRate,
    hasSubscriptionItem: eligibleItems.some((item) => item.hasSubscription),
    priceOverrideForCoverageMlModelCacheOnly,
    priceCalculationContext,
  });

  return {
    eligible: true,
    priceResult,
    reasonIneligible: undefined,
    eligibleItems,
  };
};

const getCartEligibilityInfo = (
  numValidItems: number,
  numInvalidItems: number,
  cartTotal: number,
  team: CoverageCalculatorOptions,
) => {
  const cartTotalValidity = cartTotalValidForProtection({
    cartTotal,
    minCoverageEligibleCartTotal:
      team.settings.packageProtection?.minCoverageEligibleCartTotal,
    maxCoverageEligibleCartTotal:
      team.settings.packageProtection?.maxCoverageEligibleCartTotal,
  });
  if (!cartTotalValidity.valid) {
    return { isEligible: false, reasonIneligible: cartTotalValidity.reason };
  }

  if (team.settings.excludeCoverageIfAnyInvalidProduct && numInvalidItems > 0) {
    return {
      isEligible: false,
      reasonIneligible: CoverageIneligibilityReason.CART_HAS_INVALID_PRODUCT,
    };
  }

  return { isEligible: numValidItems > 0, reasonIneligible: undefined };
};

export const packageProtectionCoverageCalculator: CoverageCalculator = {
  type: "Package Protection",
  isProductEnabled,
  getShopperEligibilityInfo,
  getItemEligibilityInfo,
  getCartEligibilityInfo,
  // need to handle non usd here.
  calculateCoveragePrice,
  getCoverage,
};
